import Button from '@orn/shared-web/components/Button/Button';
import Tooltip from '@orn/shared-web/components/Tooltip';
import { LargeSearchIcon, SettingsIcon } from '@orn/shared-web/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { PATHS } from '../../paths';
import MantleHorizontalLockupPrimary from '../logos/MantleHorizontalLockupPrimary';

import MantleAssistantNavButton from './MantleAssistantNavButton';
import NavAccountCard from './NavAccountCard';
import NavItem from './NavItem';
import NavList from './NavList';

import type { NavItemType } from './config';
import type { LoggedInUser } from '../../providers/AuthProvider';

interface IFullSidebarContentProps {
  user: LoggedInUser;
  activeUrl?: string;
  items: NavItemType[];
  footerItems?: NavItemType[];
  showAccountCard?: boolean;
  /**
   * When `true`, render the "Mantle Assistant" pill in the footer above
   * the account card. Caller (typically `MainSidebar`) is responsible
   * for resolving the `feat-assistant` gate.
   */
  showAssistantButton?: boolean;
  onOpenSearch: () => void;
}

/**
 * Inner content shared by the desktop fixed sidebar (`BaseSidebar`) and the
 * mobile nav drawer (`MobileNavDrawer`): header (Mantle wordmark + search
 * button), nav list, optional footer items, and the account card. Pure
 * presentational — receives `onOpenSearch` from a parent that owns the search
 * modal state.
 */
const FullSidebarContent = ({
  user,
  activeUrl,
  items,
  footerItems = [],
  showAccountCard = true,
  showAssistantButton = false,
  onOpenSearch,
}: IFullSidebarContentProps) => {
  const navigate = useNavigate();

  return (
    <aside className="flex h-full w-full flex-col justify-between overflow-auto pt-4 lg:pt-6">
      <div className="flex w-full items-center justify-between px-4 lg:px-5">
        <RouterLink
          to={PATHS.DASHBOARD}
          aria-label="Mantle home"
          className="outline-focus-ring flex h-8 items-center rounded-md transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <MantleHorizontalLockupPrimary className="h-8 w-auto max-w-[88px]" aria-hidden="true" />
        </RouterLink>
        <div className="flex items-center gap-0.5">
          <Tooltip title="Search">
            <Button aria-label="Search" color="tertiary" iconLeading={LargeSearchIcon} onClick={onOpenSearch} />
          </Tooltip>
          <Tooltip title="Settings">
            <Button
              aria-label="Settings"
              color="tertiary"
              iconLeading={SettingsIcon}
              onClick={() => navigate(PATHS.SETTINGS)}
            />
          </Tooltip>
        </div>
      </div>

      <NavList activeUrl={activeUrl} items={items} />

      <div className="mt-auto flex flex-col gap-4 px-2 py-4 lg:px-4 lg:py-6">
        {footerItems.length > 0 && (
          <ul className="flex flex-col">
            {footerItems.map((item) => (
              <li key={item.label} className="py-0.5">
                <NavItem
                  icon={item.icon}
                  href={item.href}
                  type="link"
                  current={(item.href?.split('?')[0] ?? '') === activeUrl}
                >
                  {item.label}
                </NavItem>
              </li>
            ))}
          </ul>
        )}

        {showAssistantButton && <MantleAssistantNavButton />}

        {showAccountCard && <NavAccountCard user={user} />}
      </div>
    </aside>
  );
};

export default FullSidebarContent;
